/**
 * Functions that return or update timekeeping model or app data only.
 */

import { getAPIData, apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";

// Getters
export async function getPendingPayoutTimesheetsCount() {
  const url = API_PATHS.TIMEKEEPING.PENDING_PAYOUT_COUNT;
  return await getAPIData(url);
}

export async function getPendingPayoutTimesheets() {
  const url = `/timekeeping/getpendingpayouttimesheetsdetails`;
  return await getAPIData(url);
}

export async function getPayoutTimesheetDashboard(params = {}) {
  const query = new URLSearchParams(params).toString();
  const url = `${API_PATHS.TIMEKEEPING.PAYOUT_DASHBOARD}${query ? `?${query}` : ""}`;
  return await getAPIData(url);
}

/**
 * Fetch full project payout-hour entries (filter Type/Range on the client).
 *
 * @param {Object} params
 * @param {number|string} params.job - JobNumber primary key (required).
 * @param {Object} [config] - Optional axios config (e.g. ``signal`` for abort).
 * @returns {Promise<Object>} ``{ job, entries, workerTypes }``.
 */
export async function getProjectPayoutHours({ job } = {}, config = {}) {
  const params = new URLSearchParams();
  if (job != null) {
    params.set("job", String(job));
  }
  const query = params.toString();
  const url = `${API_PATHS.TIMEKEEPING.PROJECT_PAYOUT_HOURS}${query ? `?${query}` : ""}`;
  return await apiRequest(url, null, { method: "GET", ...config });
}

export async function getUserSchedule(userId, date) {
  const url = `/timekeeping/getuserschedule/${userId}/${date}`;
  return await getAPIData(url);
}

export async function getUserOnShift(date) {
  const url = `/timekeeping/getuseronshift/${date}`;
  return await getAPIData(url);
}

export async function getDailyTimesheet(date) {
  const url = `/timekeeping/getdailytimesheet/${date}`;
  return await getAPIData(url);
}

export async function getWeekOfShifts(date, userId = null) {
  let url = `/timekeeping/getweekofshifts/${date}`;
  if (userId) {
    url += `?user_id=${userId}`;
  }
  return await getAPIData(url);
}

export async function getWeekOfHoursLogs(userID, weekEnding) {
  const url = `/timekeeping/getweekofhourslogs/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

export async function getPayoutTimesheetData(userID, weekEnding) {
  const url = `/timekeeping/getpayouttimesheetdata/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

export async function getShowTimesheetSigningPrompt() {
  const url = `/timekeeping/getshowtimesheetsigningprompt/`;
  return await getAPIData(url);
}

export async function getWorkerTimesheetWeekEnding() {
  const url = `/timekeeping/getworkertimesheetweekending/`;
  return await getAPIData(url);
}

export async function getWorkerTimesheetSignature(userID, weekEnding) {
  const url = `/timekeeping/getworkertimesheetsignature/${userID}/${weekEnding}`;
  return await getAPIData(url);
}

export async function checkTimesheetExists(weekEnding) {
  const url = `/timekeeping/checktimesheetexists/${weekEnding}`;
  return await getAPIData(url);
}

// Setters
export async function clockInUser(payload) {
  const url = "/timekeeping/clockinuser";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function cancelClockIn(payload) {
  const url = "/timekeeping/cancelclockin";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function clockOutUser(payload) {
  const url = "/timekeeping/clockoutuser";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function editShiftData(payload) {
  const url = "/timekeeping/updateday";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function saveShiftData(payload) {
  const url = "/timekeeping/saveshift";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function saveWorkerTimesheet(payload) {
  const url = "/timekeeping/saveworkertimesheet";
  return await apiRequest(url, payload, { method: "POST" });
}

export async function savePayoutTimesheet(payload) {
  const url = "/timekeeping/savepayouttimesheet";
  return await apiRequest(url, payload, { method: "POST" });
}
